import { css } from '@emotion/react';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { GreyButton, SecondaryButton } from 'components/atoms';
import _ from 'lodash';
import React, { forwardRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { SubmitButton as Submit } from './SubmitButton';
import { FormBuilderProps, withWrapperCSS } from './types';
import { useFormComponentUpdate, useOptions, useFields } from './useFields';
import { useFormHooks } from './useFormHooks';

const defaultInitOptions = Object.freeze({});

type Ref = HTMLDivElement;

export default forwardRef<Ref, FormBuilderProps<any> & withWrapperCSS>(
  function FormBuilder(props, ref) {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const {
      backLabel = 'Back',
      columnSpacing = 5,
      disabled = false,
      disableBackButton = false,
      disableSubmit = false,
      disableTertiary = false,
      formErrors = [],
      formFields,
      hideSubmit = false,
      initValues = {},
      onBack,
      onSubmit,
      onTertiary,
      onUpdate,
      rowSpacing = 5,
      submitLabel = 'Submit',
      submitButton: SubmitButton = Submit,
      tertiaryLabel = '',
      useFormProps = {},
      useOptionHook = (_formState) => defaultInitOptions,
      wrapperCss = {
        height: '100%',
        width: '100%',
      },
    } = props;

    const {
      formState,
      handleSubmit,
      register,
      setError,
      setValue,
      getValues,
      watch,
      unregister,
    } = useForm({
      defaultValues: initValues,
      ...useFormProps,
    });

    useEffect(() => {
      formErrors.forEach((e) => setError(e.name, e.errorOption));
    }, [formErrors]);

    const optionDict = useOptions(watch, useOptionHook);

    const onSubmitForm = (formData: any) => {
      const updates = {
        ...initValues,
        ...formData,
      };
      //console.info('Updated: ', updates);
      onSubmit(updates);
    };

    const onFormComponentUpdate = useFormComponentUpdate(setValue, onUpdate);

    useFormHooks(formFields, register, unregister, getValues);

    const fieldErrorsMap = React.useMemo(
      () =>
        formFields.reduce(
          (acc, field) => ({
            ...acc,
            [field.fieldKey]: _.get(formState.errors, field.fieldKey),
          }),
          {},
        ),
      [formState, formFields],
    );

    const fields = useFields(
      optionDict,
      fieldErrorsMap,
      initValues,
      onFormComponentUpdate,
      disabled,
      formFields,
    );

    const theForm = (
      <form
        css={css`
          height: 100%;
        `}
        onSubmit={handleSubmit(onSubmitForm)}
      >
        <Grid
          container
          direction={isSmallScreen ? 'column' : 'row'}
          rowSpacing={rowSpacing}
          columnSpacing={columnSpacing}
        >
          {fields}
        </Grid>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            padding: 5,
            paddingBottom: 8,
            justifyContent: 'flex-end',
            gap: 2,
            mt: 2,
          }}
        >
          {onBack !== undefined ? (
            <GreyButton
              variant="outlined"
              color="primary"
              onClick={onBack}
              disabled={disabled || disableBackButton}
            >
              {backLabel}
            </GreyButton>
          ) : null}
          {hideSubmit ? null : (
            <SubmitButton
              disabled={disabled || disableSubmit}
              submitLabel={submitLabel}
              data-html2canvas-ignore
            />
          )}
          {onTertiary !== undefined && tertiaryLabel.length > 0 ? (
            <SecondaryButton
              variant="outlined"
              onClick={onTertiary}
              disabled={disabled || disableTertiary}
            >
              {tertiaryLabel}
            </SecondaryButton>
          ) : null}
        </Box>
      </form>
    );

    return (
      <div style={wrapperCss} ref={ref}>
        {theForm}
      </div>
    );
  },
);
